import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { firestore } from "@/firebase/web";
import {
  RecencyPreset,
  recencyToDateFrom,
} from "@/types/mapPostsFilters";
import { Post } from "@/types/post";
import { mapDocToPost } from "@/utils/mapDocToPost.web";

const MAP_POSTS_LIMIT = 200;

type UseMapPostsArgs = { recency: RecencyPreset };

type UseMapPostsResult = {
  posts: Post[];
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useMapPosts({ recency }: UseMapPostsArgs): UseMapPostsResult {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const dateFrom = recencyToDateFrom(recency);
    const q = query(
      collection(firestore, "posts"),
      where("plateFollowersOnly", "==", false),
      where("groupId", "==", null),
      where("createdAt", ">=", Timestamp.fromDate(dateFrom)),
      orderBy("createdAt", "desc"),
      limit(MAP_POSTS_LIMIT),
    );

    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        setPosts(snap.docs.map((d) => mapDocToPost(d)));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message ?? "Failed to load map posts");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [recency, reloadKey]);

  return { posts, loading, error, reload };
}
